import {
  XAxisProps,
  YAxisProps,
  CartesianGridProps,
  TooltipProps,
  LegendProps,
  PieProps,
  ReferenceLineProps,
} from 'recharts';
import { ChartLegendContent } from '../chart-components/ChartLegendContent';

import { ChartTooltipContent } from '../chart-components/ChartTooltipContent';
import { DeltaConfig, RechartsNameType, RechartsValueType } from '../types';
import { ExtendedAreaProps } from '../chart-types/AreaChart/AreaChart';
import { ExtendedLineProps } from '../chart-types/LineChart/LineChart';
import { ExtendedBarProps } from '../chart-types/BarChart/BarChart';

function getIsDark() {
  return (
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
  );
}

function getTickLineColor() {
  return getIsDark() ? '#4b5563' : '#DDD';
}
function getGridColor() {
  return getIsDark() ? '#374151' : '#E9E9E9';
}
function getAxisTextColor() {
  return getIsDark() ? '#d1d5db' : '#666';
}
function getReferenceLineColor() {
  return getIsDark() ? '#6b7280' : '#bbb';
}

export type ChartComponentDefaultProps<TChartProps> = Omit<
  TChartProps,
  'dataKey'
>;

export type ChartDefaults = {
  line: ChartComponentDefaultProps<ExtendedLineProps>;
  area: ChartComponentDefaultProps<ExtendedAreaProps>;
  bar: ChartComponentDefaultProps<ExtendedBarProps>;
  pie: ChartComponentDefaultProps<PieProps>;
};

export type CartesianElementDefaults = {
  xAxis: React.PropsWithoutRef<Omit<XAxisProps, 'dataKey'>>;
  yAxis: React.PropsWithoutRef<YAxisProps>;
  cartesianGrid: React.PropsWithoutRef<CartesianGridProps>;
  tooltip: React.PropsWithoutRef<
    TooltipProps<RechartsValueType, RechartsNameType>
  > & {
    deltaConfig?: DeltaConfig;
  };
  legend: React.PropsWithoutRef<LegendProps>;
  referenceLine: React.PropsWithoutRef<ReferenceLineProps>;
};

export function mergeWithChartDefaults<T extends keyof ChartDefaults>(
  chartType: T,
  userConfig?: Partial<ChartDefaults[T]>,
): ChartDefaults[T] {
  return {
    ...chartDefaults[chartType],
    ...userConfig,
  };
}

export function mergeWithCartesianDefaults<
  T extends keyof CartesianElementDefaults,
>(
  elementType: T,
  userConfig?: Partial<CartesianElementDefaults[T]>,
): CartesianElementDefaults[T] {
  const defaults = getCartesianElementDefaults();
  return {
    ...defaults[elementType],
    ...userConfig,
  };
}
/**
 * Default configuration for Cartesian chart elements.
 */
export const defaultCartesianElementsConfig = {
  showCartesianGrid: true,
  showXAxis: true,
  showYAxis: true,
  showTooltip: true,
  showLegend: false,
};

export function getCartesianElementDefaults(): CartesianElementDefaults {
  return {
    xAxis: {
      fontSize: 12,
      tickLine: { stroke: getTickLineColor() },
      tickMargin: 10,
      axisLine: false,
      interval: 'equidistantPreserveStart',
      tick: { fill: getAxisTextColor() },
    },
    yAxis: {
      axisLine: false,
      fontSize: 12,
      tickMargin: 10,
      tick: { transform: 'translate(-3, 0)', fill: getAxisTextColor() },
      tickLine: { stroke: getTickLineColor() },
    },
    cartesianGrid: {
      stroke: getGridColor(),
      vertical: false,
    },
    tooltip: {
      content: ChartTooltipContent as any,
      cursor: { fillOpacity: 0.2, radius: 10 } as any,
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      content: <ChartLegendContent />,
      layout: 'vertical',
    },
    referenceLine: {
      strokeDasharray: '10 10',
      strokeWidth: 2,
      stroke: getReferenceLineColor(),
      strokeLinecap: 'round',
      fill: getReferenceLineColor(),
    },
  };
}

/** @deprecated Use getCartesianElementDefaults() for reactive dark mode support */
export const cartesianElementDefaults: CartesianElementDefaults =
  getCartesianElementDefaults();

export const chartDefaults: ChartDefaults = {
  line: {
    isAnimationActive: true,
    strokeWidth: 2,
    type: 'linear',
    dot: false,
  },
  area: {
    isAnimationActive: true,
    strokeWidth: 2,
    type: 'linear',
    gradient: true,
    gradientStartOpacity: 0.15,
    gradientStopOpacity: 0.03,
    fillOpacity: 0.1,
  },
  bar: {
    isAnimationActive: true,
    radius: 2,
  },
  pie: {
    isAnimationActive: false,
    innerRadius: '60%',
    outerRadius: '100%',
    style: {
      outline: 'none',
    },
  },
};

export const defaultDeltaConfig: DeltaConfig = {
  showDelta: true,
  isNeutralStyling: false,
  isCompactNumberFormat: true,
};
